import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Search, FileDown, Mail, Loader2, ShieldCheck, Layers, EyeOff, Paperclip } from "lucide-react";
import { PriorityBadge, DiscBadge } from "@/components/badges";

const inputCls = "w-full bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-2 text-sm outline-none focus:border-[#0EA5E9] font-mono text-[#F3F4F6]";

export const CallEventReport = ({ isAdmin, canBatch }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [batch, setBatch] = useState({ date_from: "", date_to: "", address: "" });
  const [batchBusy, setBatchBusy] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);

  const exportBatch = async () => {
    if (!batch.date_from && !batch.date_to && !batch.address.trim()) { toast.error("Enter a date range and/or an address"); return; }
    setBatchBusy(true);
    try {
      const params = Object.fromEntries(Object.entries(batch).filter(([, v]) => v && v.trim()));
      const r = await client.get("/call-event/batch.pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = (r.headers["content-disposition"] || "").match(/filename="?([^";]+)/)?.[1] || "CallEvent_Batch.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Batch packet downloaded");
    } catch (e) {
      const txt = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : e.response?.data?.detail;
      toast.error(txt || "Batch export failed");
    } finally { setBatchBusy(false); }
  };

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setBusy(true); setSel(null);
    try {
      const { data } = await client.get("/calls/search", { params: { q: q.trim(), page_size: 25 } });
      const rows = data.results || [];
      setResults(Array.isArray(rows) ? rows : []);
      if (!rows?.length) toast("No calls matched");
    } catch { toast.error("Search failed"); }
    setBusy(false);
  };

  const pick = async (r) => {
    try { const { data } = await client.get(`/call-event/${r.id}`); setSel(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Unable to load call"); }
  };

  const fetchPdf = async (redacted = false) => {
    setPdfBusy(true);
    try {
      const r = await client.get(`/call-event/${sel.id}/report.pdf`, { params: redacted ? { redacted: 1 } : {}, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `CallEvent_${sel.cc_number}${redacted ? "_REDACTED" : ""}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      return true;
    } catch { toast.error("PDF generation failed"); return false; }
    finally { setPdfBusy(false); }
  };

  const email = async () => {
    if (!(await fetchPdf())) return;
    const subject = encodeURIComponent(`CAD Call Event Report — CC# ${sel.cc_number} (LAW ENFORCEMENT SENSITIVE)`);
    const body = encodeURIComponent(
      `Attached: CAD Call Event Report for CC# ${sel.cc_number}.\n\nCall type: ${sel.call_type_label}\nLocation: ${sel.address || "—"}\nReceived: ${sel.created_at?.slice(0, 16).replace("T", " ")} UTC\nUnits: ${(sel.units || []).join(", ") || "—"}\n\nCLASSIFICATION: LAW ENFORCEMENT SENSITIVE // FOR OFFICIAL USE ONLY. Dissemination restricted to authorized criminal-justice personnel per CJIS Security Policy. The PDF (downloaded to your device) is stamped with a SHA-256 integrity hash on every page — attach it to this message before sending.`);
    toast.success("PDF downloaded — attach it to the email that just opened");
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div data-testid="rms-call-event-panel" className="space-y-3">
      <div className="flex items-start gap-3 p-3 border border-[#2C2D35] bg-[#0d0e11]">
        <img src="/brand/records-seal.png" alt="Matcom Records Division" data-testid="call-event-seal" className="w-14 h-14 rounded-full shrink-0" />
        <div className="text-xs text-[#9CA3AF] font-mono">
          <b className="text-[#F3F4F6]"><ShieldCheck size={12} className="inline text-[#0EA5E9] mr-1 -mt-0.5" />MATCOM RECORDS DIVISION — CAD CALL EVENT REPORT</b> — pulls the complete call for service record (summary, location, caller, units, full chronological event log, GPS track, disposition) into a
          government-grade PDF with classification banner, page numbering, and SHA-256 integrity hash on every page.
        </div>
      </div>
      <form onSubmit={search} className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-2.5 text-[#6B7280]" />
          <input data-testid="call-event-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="CC# (e.g. 2026-12345), public ref, address, call type…" className={inputCls + " pl-8"} />
        </div>
        <button data-testid="call-event-search-btn" type="submit" disabled={busy} className="px-4 py-2 font-head text-sm bg-[#0EA5E9] text-black hover:bg-[#38BDF8] disabled:opacity-50 flex items-center gap-2">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} PULL CALL
        </button>
      </form>

      {canBatch && (
        <div data-testid="call-event-batch" className="border border-[#F59E0B]/40 bg-[#0d0e11] p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs font-mono"><Layers size={14} className="text-[#F59E0B]" /><b className="text-[#F3F4F6]">BATCH EXPORT</b><span className="text-[#9CA3AF]">— every call in a date range and/or at an address, combined into one PDF packet with cover index (max 100 calls)</span></div>
          <div className="grid sm:grid-cols-4 gap-2">
            <div><label className="text-[9px] font-mono text-[#6B7280]">FROM DATE</label><input data-testid="batch-date-from" type="date" value={batch.date_from} onChange={(e) => setBatch({ ...batch, date_from: e.target.value })} className={inputCls} /></div>
            <div><label className="text-[9px] font-mono text-[#6B7280]">TO DATE</label><input data-testid="batch-date-to" type="date" value={batch.date_to} onChange={(e) => setBatch({ ...batch, date_to: e.target.value })} className={inputCls} /></div>
            <div><label className="text-[9px] font-mono text-[#6B7280]">ADDRESS CONTAINS</label><input data-testid="batch-address" value={batch.address} onChange={(e) => setBatch({ ...batch, address: e.target.value })} placeholder="e.g. 3300 Harford Rd" className={inputCls} /></div>
            <div className="flex items-end"><button data-testid="batch-export-btn" onClick={exportBatch} disabled={batchBusy} className="w-full px-4 py-2 font-head text-sm bg-[#F59E0B] text-black hover:bg-[#FBBF24] disabled:opacity-50 flex items-center gap-2 justify-center">{batchBusy ? <Loader2 size={14} className="animate-spin" /> : <Layers size={14} />} EXPORT PACKET</button></div>
          </div>
        </div>
      )}

      {results.length > 0 && !sel && (
        <table className="w-full text-left" data-testid="call-event-results">
          <thead><tr className="text-[10px] font-mono text-[#6B7280] border-b border-[#2C2D35]"><th className="py-1.5 px-2">CC#</th><th className="px-2">Type</th><th className="px-2">Location</th><th className="px-2">Received</th><th className="px-2">Status</th></tr></thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.id} data-testid={`call-event-row-${r.cc_number}`} onClick={() => pick(r)} className="border-b border-[#1f2027] hover:bg-[#141518] cursor-pointer text-xs">
                <td className="py-2 px-2 font-mono text-[#0EA5E9] font-bold">{r.cc_number}</td>
                <td className="px-2 flex items-center gap-1.5"><DiscBadge discipline={r.discipline} /><PriorityBadge priority={r.priority} />{r.call_type_label}</td>
                <td className="px-2 text-[#9CA3AF]">{r.location?.address || r.address || "—"}</td>
                <td className="px-2 font-mono text-[#9CA3AF]">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-2 font-mono uppercase">{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {sel && (
        <div data-testid="call-event-detail" className="border border-[#2C2D35] bg-[#0d0e11] p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-mono text-[10px] text-[#6B7280]">CALL FOR SERVICE</div>
              <div className="font-head text-xl text-[#F3F4F6]">CC# {sel.cc_number} <span className="text-sm text-[#9CA3AF]">· {sel.public_ref}</span></div>
              <div className="text-sm text-[#9CA3AF] mt-1 flex items-center gap-2"><DiscBadge discipline={sel.discipline} /><PriorityBadge priority={sel.priority} />{sel.call_type_label}</div>
              <div className="text-xs font-mono text-[#9CA3AF] mt-1">{sel.address || "—"}</div>
              <div className="text-xs font-mono text-[#6B7280] mt-1">Received {sel.created_at?.slice(0, 16).replace("T", " ")} UTC · {sel.closed_at ? `Closed ${sel.closed_at.slice(0, 16).replace("T", " ")} UTC` : "OPEN"} · {sel.events} event log entries · Units: {(sel.units || []).join(", ") || "—"}{sel.rms_report ? ` · RMS ${sel.rms_report}` : ""}</div>
            </div>
            <div className="flex flex-col gap-2">
              <button data-testid="call-event-download-pdf" onClick={() => fetchPdf(false)} disabled={pdfBusy} className="px-4 py-2 font-head text-sm bg-[#0EA5E9] text-black hover:bg-[#38BDF8] disabled:opacity-50 flex items-center gap-2 justify-center">
                {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} DOWNLOAD PDF
              </button>
              <button data-testid="call-event-download-redacted" onClick={() => fetchPdf(true)} disabled={pdfBusy} title="Caller identity, phones/ALI, personnel names, precise address, GPS and CJIS identifiers masked" className="px-4 py-2 font-head text-sm border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-50 flex items-center gap-2 justify-center">
                <EyeOff size={14} /> REDACTED PUBLIC COPY
              </button>
              {canBatch && (
                <button data-testid="call-event-attach-open" onClick={() => setAttachOpen((v) => !v)} className="px-4 py-2 font-head text-sm border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/10 flex items-center gap-2 justify-center">
                  <Paperclip size={14} /> ATTACH TO RECORDS REQUEST
                </button>
              )}
              {isAdmin && (
                <button data-testid="call-event-email" onClick={email} disabled={pdfBusy} className="px-4 py-2 font-head text-sm border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10 disabled:opacity-50 flex items-center gap-2 justify-center">
                  <Mail size={14} /> EMAIL REPORT (ADMIN)
                </button>
              )}
              <button data-testid="call-event-back" onClick={() => setSel(null)} className="text-xs font-mono text-[#6B7280] hover:text-[#F3F4F6]">← back to results</button>
            </div>
          </div>
          {attachOpen && canBatch && (
            <AttachToRequest callId={sel.id} cc={sel.cc_number} onDone={() => setAttachOpen(false)} />
          )}
        </div>
      )}
    </div>
  );
};

const AttachToRequest = ({ callId, cc, onDone }) => {
  const [reqs, setReqs] = useState([]);
  const [requestNo, setRequestNo] = useState("");
  const [redacted, setRedacted] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    client.get("/ia/records-requests").then(({ data }) => {
      const open = (data || []).filter((r) => r.status !== "closed" && !r.purged);
      setReqs(open); if (open[0]) setRequestNo(open[0].request_no);
    }).catch(() => setReqs([]));
  }, []);
  const attach = async () => {
    if (!requestNo) { toast.error("Select a pending records request"); return; }
    if (!redacted && !window.confirm("Release the UNREDACTED report (caller identity, personnel names, GPS, CJIS data) to this requester?")) return;
    setBusy(true);
    try {
      const { data } = await client.post(`/call-event/${callId}/attach-to-records-request`, { request_no: requestNo, redacted, note });
      toast.success(`${data.attachment.filename} released to ${data.request_no} — requester can download it from their tracker`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Attach failed"); }
    setBusy(false);
  };
  return (
    <div data-testid="call-event-attach-panel" className="mt-3 p-3 border border-[#22C55E]/40 bg-[#0d0e11] space-y-2">
      <div className="text-xs font-mono text-[#9CA3AF]"><b className="text-[#F3F4F6]">RELEASE TO RECORDS REQUEST</b> — the PDF is generated now, stored with the request, and the requester is notified in their tracker.</div>
      <div className="grid sm:grid-cols-3 gap-2">
        <div className="sm:col-span-2">
          <label className="text-[9px] font-mono text-[#6B7280]">PENDING REQUEST</label>
          <select data-testid="attach-request-select" value={requestNo} onChange={(e) => setRequestNo(e.target.value)} className={inputCls}>
            {!reqs.length && <option value="">No pending records requests</option>}
            {reqs.map((r) => <option key={r.request_no} value={r.request_no}>{r.request_no} · {r.name} · {r.record_types.join(", ")}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[9px] font-mono text-[#6B7280]">VERSION</label>
          <select data-testid="attach-version-select" value={redacted ? "redacted" : "full"} onChange={(e) => setRedacted(e.target.value === "redacted")} className={inputCls}>
            <option value="redacted">Redacted public copy (recommended)</option>
            <option value="full">Unredacted (authorized release)</option>
          </select>
        </div>
      </div>
      <input data-testid="attach-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to requester (optional)" className={inputCls} />
      <div className="flex gap-2">
        <button data-testid="attach-submit" onClick={attach} disabled={busy || !reqs.length} className="px-4 py-2 font-head text-sm bg-[#22C55E] text-black hover:bg-[#4ADE80] disabled:opacity-50 flex items-center gap-2">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} RELEASE {cc}
        </button>
        <button onClick={onDone} className="px-3 py-2 text-xs font-mono text-[#6B7280] hover:text-[#F3F4F6]">cancel</button>
      </div>
    </div>
  );
};
